<template>
  <section class="assembly-calendar-section">
    <div class="calendar-content">
      <div class="calendar-header">
        <page-title eyebrow="ASSEMBLEIA DE CREDORES" icon="mdi-calendar-month-outline" title="Calendário" highlight="de assembleias" />

      </div>

      <div class="calendar-layout">
        <div class="agenda-card">
          <button
            v-for="event in visibleEvents"
            :key="event.id"
            type="button"
            class="agenda-item"
            @click="selectAgendaEvent(event)"
          >
            <span class="agenda-date">
              <strong>{{ formatEventDay(event.date) }}</strong>
              <small>{{ formatEventMonth(event.date) }}</small>
            </span>

            <span class="agenda-main">
              <strong>{{ event.client }}</strong>
              <span>
                <em :class="['agenda-tag', event.kind]">{{ event.kindLabel }}</em>
                <small v-if="event.sessionLabel">{{ event.sessionLabel }}</small>
                <small>{{ formatEventTime(event.date) }}</small>
                <small>{{ event.locationLabel }}</small>
              </span>
            </span>

            <span class="agenda-action" aria-hidden="true">→</span>
          </button>

          <div v-if="!visibleEvents.length" class="agenda-empty">
            Nenhuma assembleia encontrada.
          </div>
        </div>

        <aside class="mini-calendar-panel">
          <div class="mini-calendar-surface">
            <div class="mini-calendar-top">
              <strong>{{ monthTitle }}</strong>

              <div>
                <button type="button" @click="changeMonth(-1)" aria-label="Mês anterior">‹</button>
                <button type="button" @click="changeMonth(1)" aria-label="Próximo mês">›</button>
              </div>
            </div>

            <div class="mini-calendar-grid calendar-weekdays">
              <span v-for="day in weekdays" :key="day">{{ day }}</span>
            </div>

            <div class="mini-calendar-grid">
              <button
                v-for="day in monthDays"
                :key="day.key"
                type="button"
                :class="['mini-calendar-day', {
                  muted: !day.currentMonth,
                  today: day.isToday,
                  selected: day.key === selectedDate,
                  hasEvent: eventDates[day.key]
                }]"
                @click="selectedDate = day.key"
              >
                {{ day.label }}
              </button>
            </div>
          </div>

          <div class="calendar-legend">
            <span><i class="agc"></i> AGC - assembleia geral de credores</span>
          </div>
        </aside>
      </div>

      <v-dialog v-model="selectedOpen" width="1120px" content-class="assembly-details-dialog">
        <div v-if="selectedEvent" class="assembly-details-modal">
          <aside class="assembly-details-sidebar">
            <span class="assembly-details-eyebrow">Assembleia Geral de Credores</span>

            <strong class="assembly-details-date">{{ formatEventDate(selectedEvent.date) }}</strong>
            <span class="assembly-details-time">{{ formatEventYear(selectedEvent.date) }} · {{ formatEventTime(selectedEvent.date) }} Brasília</span>

            <span :class="['assembly-details-status', meetingDisplayStatus(selectedEvent).className]">
              {{ meetingDisplayStatus(selectedEvent).label }}
            </span>

            <div class="assembly-details-divider"></div>

            <dl class="assembly-details-list">
              <div>
                <dt>Convocação</dt>
                <dd>{{ selectedEvent.name || 'Assembleia cadastrada' }}</dd>
                <dd v-if="selectedEvent.sessionLabel" class="assembly-details-type">Tipo: {{ selectedEvent.sessionLabel }}</dd>
              </div>

              <div>
                <dt>Modalidade</dt>
                <dd>{{ selectedEvent.locationLabel }}</dd>
              </div>

              <div v-if="selectedEvent.accreditationEnabled">
                <dt>Credenciamento</dt>
                <dd>{{ accreditationText(selectedEvent) }}</dd>
              </div>
            </dl>

            <button type="button" class="assembly-details-primary" @click="openDetailsPage">
              {{ primaryActionLabel(selectedEvent) }} <span>↗</span>
            </button>
          </aside>

          <section class="assembly-details-content">
            <button type="button" class="assembly-details-close" aria-label="Fechar" @click="selectedOpen = false">
              ×
            </button>

            <h2>
              <span>detalhes da</span>
              <strong>assembleia</strong>
            </h2>

            <div class="assembly-details-description" v-html="selectedDescription"></div>

            <div class="assembly-client-box">
              <div class="assembly-client-header">
                <strong>Recuperanda(s)</strong>
              </div>

              <p>{{ selectedClientNames }}</p>
            </div>

            <div class="assembly-details-actions">
              <button
                v-if="selectedEvent.clientId"
                type="button"
                class="assembly-details-secondary"
                @click="openCasePage"
              >
                ver processo completo
              </button>
              <button type="button" class="assembly-details-text" @click="selectedOpen = false">
                fechar
              </button>
            </div>
          </section>
        </div>
      </v-dialog>
    </div>
  </section>
</template>

<script>
import axios from 'axios';

import generalMeetingStatus from "@/common/data/general-meeting-status.data";

export default {
  name: 'CalendarSection',

  data: () => ({
    selectedEvent: null,
    selectedOpen: false,
    selectedDate: null,
    currentMonth: null,
    events: [],
    weekdays: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']
  }),

  mounted() {
    this.currentMonth = this.$moment().startOf('month');
    this.selectedDate = this.$moment().format('YYYY-MM-DD');
    this.fetchEvents();
  },

  computed: {
    monthTitle() {
      return this.currentMonth ? this.currentMonth.format('MMMM YYYY') : '';
    },

    eventDates() {
      return this.events.reduce((result, event) => {
        result[event.start] = true;
        return result;
      }, {});
    },

    selectedDescription() {
      if (!this.selectedEvent || !this.selectedEvent.description) {
        return 'As informações completas desta assembleia estão disponíveis na página de detalhes da AGC.';
      }

      return this.selectedEvent.description;
    },

    selectedClientNames() {
      if (!this.selectedEvent) return '';

      if (this.selectedEvent.companies.length) {
        return this.selectedEvent.companies.map(company => company.name).join(' · ');
      }

      return this.selectedEvent.client;
    },

    selectedCompanyCount() {
      if (!this.selectedEvent) return 0;
      return this.selectedEvent.companies.length || 1;
    },

    visibleEvents() {
      const events = this.selectedDate
        ? this.events.filter(event => event.start >= this.selectedDate)
        : this.events;

      return events.slice(0, 6);
    },

    monthDays() {
      if (!this.currentMonth) return [];

      const start = this.currentMonth.clone().startOf('month').startOf('week');
      const end = this.currentMonth.clone().endOf('month').endOf('week');
      const days = [];
      const cursor = start.clone();

      while (cursor.isSameOrBefore(end, 'day')) {
        days.push({
          key: cursor.format('YYYY-MM-DD'),
          label: cursor.format('D'),
          currentMonth: cursor.isSame(this.currentMonth, 'month'),
          isToday: cursor.isSame(this.$moment(), 'day')
        });

        cursor.add(1, 'day');
      }

      return days;
    }
  },

  methods: {
    openCasePage() {
      if (!this.selectedEvent || !this.selectedEvent.clientId) return;

      this.selectedOpen = false;
      this.$router.push({ name: 'CasesDetails', params: { id: this.selectedEvent.clientId } });
      this.$nextTick(() => window.scrollTo(0, 0));
    },

    openDetailsPage() {
      this.$router.push({
        path: '/assembleia-credores',
        query: { eventId: this.selectedEvent.id }
      });
    },

    async fetchEvents() {
      try {
        const response = await axios.get('/api/general-meetings', {
          params: { limit: 10000 }
        });

        this.events = response.data.content
          .map(event => {
            const formattedStartDate = this.getLocalDateKey(event.date);
            const companies = event.client && Array.isArray(event.client.companies)
              ? event.client.companies
              : [];

            return {
              id: event.id,
              name: event.name,
              client: event.client ? event.client.name : 'Recuperanda não informada',
              clientId: event.client ? event.client.id : null,
              companies: companies,
              start: formattedStartDate || '2024-01-20',
              kind: 'agc',
              kindLabel: 'AGC',
              status: event.status,
              date: event.date,
              description: event.description,
              accreditationEnabled: event.accreditation_enabled === true || Number(event.accreditation_enabled) === 1,
              accreditationStartsAt: event.accreditation_starts_at || event.accreditation_start_at || null,
              locationLabel: this.resolveLocationLabel(event),
              // 1ª Convocação, 2ª Convocação ou Prosseguimento N (calculado na API).
              sessionLabel: event.session_label || null
            };
          })
          .sort((a, b) => String(a.start).localeCompare(String(b.start)));
      } catch (error) {
        this.events = [];
      }
    },

    getLocalDateKey(date) {
      if (!date) return null;

      if (typeof date === 'string') {
        const dateMatch = date.match(/^(\d{4}-\d{2}-\d{2})/);
        if (dateMatch) return dateMatch[1];
      }

      const parsedDate = this.$moment(date);
      return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null;
    },

    changeMonth(direction) {
      this.currentMonth = this.currentMonth.clone().add(direction, 'month');
    },

    selectAgendaEvent(event) {
      this.selectedEvent = event;
      this.selectedOpen = true;
    },

    formatEventDay(date) {
      const parsedDate = this.$moment(date);
      return parsedDate.isValid() ? parsedDate.format('DD') : '--';
    },

    formatEventMonth(date) {
      const parsedDate = this.$moment(date);
      return parsedDate.isValid() ? parsedDate.format('MMM').replace('.', '') : '';
    },

    formatEventDate(date) {
      const parsedDate = this.$moment(date);
      return parsedDate.isValid() ? parsedDate.format('DD/MM') : '--/--';
    },

    formatEventYear(date) {
      const parsedDate = this.$moment(date);
      return parsedDate.isValid() ? parsedDate.format('YYYY') : '----';
    },

    formatEventTime(date) {
      const parsedDate = this.$moment(date);
      return parsedDate.isValid() ? parsedDate.format('HH[h]mm') : '-';
    },

    accreditationText(event) {
      if (event.accreditationStartsAt) {
        const accreditationDate = this.$moment(event.accreditationStartsAt);

        if (accreditationDate.isAfter(this.$moment())) {
          return `abre em ${accreditationDate.format('DD/MM')} às ${this.formatEventTime(accreditationDate)}`;
        }

        if (this.isMeetingFinished(event.status)) return 'encerrado';

        return `aberto desde ${this.formatEventTime(accreditationDate)}`;
      }

      if (this.isMeetingFinished(event.status)) return 'encerrado';

      const meetingDate = this.$moment(event.date);

      if (meetingDate.isValid() && meetingDate.isAfter(this.$moment(), 'day')) {
        return 'ainda não iniciado';
      }

      if (Number(event.status) === 5) return 'em andamento';

      return 'horário não informado';
    },

    isMeetingFinished(status) {
      return [1, 2, 4].includes(Number(status));
    },

    meetingDisplayStatus(event) {
      const normalizedStatus = Number(event.status);
      const meetingDate = this.$moment(event.date);

      if (normalizedStatus === 0 && meetingDate.isValid() && meetingDate.isAfter(this.$moment())) {
        return { label: 'Agendada', className: 'is-warning' };
      }

      if (normalizedStatus === 5) {
        return { label: 'Em andamento', className: 'is-success' };
      }

      const statusObject = generalMeetingStatus.find(status => status.id === normalizedStatus);

      return {
        label: statusObject ? statusObject.name : 'Status não informado',
        className: this.isMeetingFinished(normalizedStatus) ? 'is-closed' : 'is-warning'
      };
    },

    primaryActionLabel(event) {
      const meetingDate = this.$moment(event.date);
      const isAvailable = Number(event.status) === 5
        || (Number(event.status) === 0 && meetingDate.isValid() && !meetingDate.isAfter(this.$moment()));

      return isAvailable ? 'acessar AGC' : 'ver detalhes da AGC';
    },

    resolveLocationLabel(event) {
      const explicitLocation = event.location_label || event.location || event.modality;

      if (typeof explicitLocation === 'string' && explicitLocation.trim()) {
        return explicitLocation.trim();
      }

      if (event.type === 1) return 'Presencial';

      if (
        event.type === 2
        || event.zoom_enabled
        || event.zoom_join_url
        || /zoom\.us|plataforma virtual|\bvirtual\b/i.test(event.description || '')
      ) {
        return 'Virtual';
      }

      return 'Não informada';
    },

    generalMeetingStatusColor(status) {
      switch (status) {
        case 0:
        case 1:
          return 'primary';
        case 2:
          return 'error';
        case 3:
          return 'warning';
        case 4:
          return 'grey';
        case 5:
          return 'green';
        default:
          return 'primary';
      }
    }
  }
};
</script>

<style>
.assembly-calendar-section {
  padding: 64px 0 78px;
  background: #fff;
}

.calendar-content {
  width: min(100% - 64px, 1360px);
  margin: 0 auto;
}

.calendar-header .section-page-title {
  margin: 0;
}

.calendar-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.58fr) minmax(330px, 0.82fr);
  gap: 32px;
  margin-top: 34px;
}

.agenda-card {
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(32, 0, 51, 0.08);
  border-radius: 4px;
  box-shadow: 0 16px 36px rgba(32, 0, 51, 0.05);
}

.agenda-item {
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr) auto;
  gap: 24px;
  align-items: center;
  width: 100%;
  min-height: 112px;
  padding: 22px 26px;
  color: var(--v-darkSecondary-base);
  background: #fff;
  border: 0;
  border-bottom: 1px solid rgba(32, 0, 51, 0.08);
  text-align: left;
  cursor: pointer;
  transition: background 0.16s ease;
}

.agenda-item:hover {
  background: rgba(117, 87, 223, 0.04);
}

.agenda-date {
  display: grid;
  align-items: center;
  justify-items: center;
  width: 70px;
  height: 78px;
  color: var(--v-darkSecondary-base);
  background: rgba(117, 87, 223, 0.1);
  border-radius: 4px;
}

.agenda-date strong {
  font-family: 'Metropolis' !important;
  font-size: 30px;
  font-weight: 400;
  line-height: 1;
}

.agenda-date small {
  color: var(--v-primary-base);
  font-family: 'Metropolis' !important;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.agenda-main {
  display: grid;
  gap: 11px;
  min-width: 0;
}

.agenda-main > strong {
  overflow: hidden;
  color: var(--v-darkSecondary-base);
  font-family: 'Metropolis' !important;
  font-size: clamp(18px, 1.55vw, 22px);
  font-weight: 800;
  line-height: 1.28;
  text-overflow: ellipsis;
}

.agenda-main > span {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  color: rgba(32, 0, 51, 0.48);
  font-family: 'Metropolis' !important;
  font-size: 15px;
  font-weight: 500;
}

.agenda-tag {
  padding: 5px 11px;
  color: #fff;
  background: var(--v-primary-base);
  border-radius: 4px;
  font-style: normal;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.agenda-tag.voting {
  background: #c64f92;
}

.agenda-tag.deadline {
  background: #d18a2e;
}

.agenda-tag.publication {
  background: #4aa5a2;
}

.agenda-action {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  color: var(--v-primary-base);
  background: rgba(117, 87, 223, 0.1);
  border: 1px solid rgba(117, 87, 223, 0.18);
  border-radius: 50%;
  font-family: 'Metropolis' !important;
  font-size: 24px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  transition: color 0.16s ease, background 0.16s ease, transform 0.16s ease;
}

.agenda-item:hover .agenda-action {
  color: #fff;
  background: var(--v-primary-base);
  transform: translateX(3px);
}

.agenda-empty {
  padding: 34px;
  color: rgba(32, 0, 51, 0.52);
  font-family: 'Metropolis' !important;
  font-size: 15px;
}

.mini-calendar-panel {
  align-self: start;
}

.mini-calendar-surface {
  overflow: hidden;
  background: var(--v-darkSecondary-base);
  border-radius: 4px;
  box-shadow: 0 16px 36px rgba(32, 0, 51, 0.08);
}

.mini-calendar-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 30px 32px 16px;
  color: #fff;
}

.mini-calendar-top strong {
  font-family: 'Metropolis' !important;
  font-size: 20px;
  font-weight: 800;
  text-transform: lowercase;
}

.mini-calendar-top button {
  color: rgba(255, 255, 255, 0.7);
  background: transparent;
  border: 0;
  font-size: 28px;
  cursor: pointer;
}

.mini-calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 9px;
  padding: 0 32px 22px;
  background: var(--v-darkSecondary-base);
}

.calendar-weekdays {
  color: rgba(255, 255, 255, 0.36);
  font-family: 'Metropolis' !important;
  font-size: 13px;
  font-weight: 800;
  text-align: center;
}

.mini-calendar-day {
  position: relative;
  display: grid;
  place-items: center;
  min-height: 35px;
  color: rgba(255, 255, 255, 0.78);
  background: transparent;
  border: 0;
  border-radius: 4px;
  font-family: 'Metropolis' !important;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}

.mini-calendar-day.muted {
  color: rgba(255, 255, 255, 0.28);
}

.mini-calendar-day.today,
.mini-calendar-day.selected {
  color: #fff;
  background: var(--v-primary-base);
}

.mini-calendar-day.hasEvent::after {
  content: '';
  position: absolute;
  bottom: 1px;
  width: 5px;
  height: 5px;
  background: var(--v-primary-base);
  border-radius: 50%;
}

.calendar-legend {
  display: grid;
  gap: 12px;
  padding: 20px 0 0;
  color: rgba(32, 0, 51, 0.56);
  font-family: 'Metropolis' !important;
  font-size: 14px;
  font-weight: 600;
}

.calendar-legend span {
  display: flex;
  align-items: center;
  gap: 12px;
}

.calendar-legend i {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.calendar-legend .agc {
  background: var(--v-primary-base);
}

.title-assembly {
  font-size: 18px;
  color: var(--v-primary-base) !important;
}

.assembly-details-dialog {
  border-radius: 8px;
  box-shadow: none;
}

.assembly-details-modal {
  display: grid;
  grid-template-columns: minmax(300px, 0.78fr) minmax(0, 1.22fr);
  overflow: hidden;
  color: var(--v-darkSecondary-base);
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 24px 70px rgba(32, 0, 51, 0.24);
}

.assembly-details-sidebar {
  display: flex;
  flex-direction: column;
  min-height: 540px;
  padding: 40px 34px 34px;
  color: #fff;
  background: #210033;
}

.assembly-details-eyebrow,
.assembly-details-list dt,
.assembly-client-header strong {
  font-family: 'Metropolis' !important;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.assembly-details-eyebrow {
  color: rgba(255, 255, 255, 0.72);
}

.assembly-details-date {
  margin-top: 24px;
  font-family: 'Metropolis' !important;
  font-size: clamp(42px, 4vw, 54px);
  font-weight: 300;
  line-height: 1;
}

.assembly-details-time {
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.76);
  font-family: 'Metropolis' !important;
  font-size: 16px;
  font-weight: 700;
}

.assembly-details-status {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 10px;
  margin-top: 26px;
  padding: 8px 14px;
  color: #58d99b;
  background: rgba(88, 217, 155, 0.16);
  border-radius: 999px;
  font-family: 'Metropolis' !important;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.assembly-details-status::before {
  content: '';
  width: 9px;
  height: 9px;
  background: currentColor;
  border-radius: 50%;
}

.assembly-details-status.is-closed {
  color: rgba(255, 255, 255, 0.78);
  background: rgba(255, 255, 255, 0.14);
}

.assembly-details-status.is-warning {
  color: #ffca72;
  background: rgba(255, 202, 114, 0.16);
}

.assembly-details-status.is-success {
  color: #8df0bc;
  background: rgba(141, 240, 188, 0.15);
}

.assembly-details-divider {
  height: 1px;
  margin: 30px 0;
  background: rgba(255, 255, 255, 0.16);
}

.assembly-details-list {
  display: grid;
  gap: 24px;
  margin: 0 0 32px;
}

.assembly-details-list div {
  display: grid;
  gap: 9px;
}

.assembly-details-list dd.assembly-details-type {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 14px;
  font-weight: 600;
}

.assembly-details-list dt {
  color: rgba(255, 255, 255, 0.48);
}

.assembly-details-list dd {
  margin: 0;
  color: #fff;
  font-family: 'Metropolis' !important;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.32;
}

.assembly-details-primary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  margin-top: auto;
  padding: 17px 22px;
  color: #fff;
  background: #7258dc;
  border: 0;
  border-radius: 4px;
  font-family: 'Metropolis' !important;
  font-size: 17px;
  font-weight: 900;
  cursor: pointer;
  transition: filter 0.16s ease, transform 0.16s ease;
}

.assembly-details-primary:hover {
  background: #8067e6;
  transform: translateY(-1px);
}

.assembly-details-primary span {
  font-size: 24px;
  line-height: 1;
}

.assembly-details-content {
  position: relative;
  padding: 40px 44px 34px;
  background: #faf9fc;
}

.assembly-details-close {
  position: absolute;
  top: 22px;
  right: 24px;
  width: 34px;
  height: 34px;
  color: rgba(32, 0, 51, 0.42);
  background: transparent;
  border: 0;
  font-size: 30px;
  line-height: 1;
  cursor: pointer;
}

.assembly-details-content h2 {
  margin: 0;
  font-family: 'Metropolis' !important;
  font-size: 30px;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 0.12em;
  text-transform: lowercase;
}

.assembly-details-content h2 span {
  margin-right: 6px;
  color: var(--v-primary-base);
}

.assembly-details-content h2 strong {
  display: inline-block;
  position: relative;
  color: var(--v-darkSecondary-base);
  font-weight: 700;
  letter-spacing: 0.08em;
}

.assembly-details-content h2 strong::after {
  content: '';
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 2px;
  background: currentColor;
}

.assembly-details-description {
  margin-top: 24px;
  color: rgba(32, 0, 51, 0.62);
  font-family: 'Metropolis' !important;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.58;
}

.assembly-details-description p {
  margin-bottom: 10px;
}

.assembly-client-box {
  overflow: hidden;
  margin-top: 26px;
  border: 1px solid rgba(32, 0, 51, 0.12);
  border-radius: 8px;
  background: #fff;
}

.assembly-client-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  background: #f8f6fb;
}

.assembly-client-header strong {
  color: var(--v-darkSecondary-base);
}

.assembly-client-box p {
  margin: 0;
  padding: 18px 22px 20px;
  color: rgba(32, 0, 51, 0.68);
  font-family: 'Metropolis' !important;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
}

.assembly-details-actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 20px;
  align-items: center;
  margin-top: 28px;
}

.assembly-details-secondary {
  width: 100%;
  padding: 15px 22px;
  color: var(--v-primary-base);
  background: #fff;
  border: 1px solid rgba(117, 87, 223, 0.42);
  border-radius: 4px;
  font-family: 'Metropolis' !important;
  font-size: 14px;
  font-weight: 900;
  cursor: pointer;
}

.assembly-details-secondary:hover {
  border-color: var(--v-primary-base);
}

.assembly-details-text {
  color: rgba(32, 0, 51, 0.46);
  background: transparent;
  border: 0;
  font-family: 'Metropolis' !important;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

@media (max-width: 1100px) {
  .calendar-layout {
    grid-template-columns: 1fr;
  }

  .mini-calendar-panel {
    max-width: 520px;
  }

  .assembly-details-modal {
    grid-template-columns: 1fr;
  }

  .assembly-details-sidebar {
    min-height: auto;
  }
}

@media (max-width: 680px) {
  .assembly-calendar-section {
    padding: 48px 0 58px;
  }

  .calendar-content {
    width: min(100% - 28px, 1780px);
  }

  .calendar-header h1 {
    font-size: 24px;
    letter-spacing: 0.2em;
  }

  .calendar-header h1 strong {
    display: block;
    width: max-content;
    margin-left: 0;
    margin-top: 4px;
  }

  .agenda-item {
    grid-template-columns: 76px minmax(0, 1fr);
    gap: 18px;
    padding: 22px 16px;
  }

  .agenda-date {
    width: 66px;
    height: 72px;
  }

  .agenda-action {
    grid-column: 2;
    justify-self: start;
    width: 36px;
    height: 36px;
    font-size: 21px;
  }

  .mini-calendar-top,
  .mini-calendar-grid {
    padding-left: 18px;
    padding-right: 18px;
  }

  .assembly-details-dialog {
    margin: 16px;
  }

  .assembly-details-sidebar,
  .assembly-details-content {
    padding: 30px 24px;
  }

  .assembly-details-date {
    font-size: 42px;
  }

  .assembly-details-time,
  .assembly-details-list dd,
  .assembly-details-primary {
    font-size: 15px;
  }

  .assembly-details-content h2 {
    padding-right: 30px;
    font-size: 28px;
  }

  .assembly-details-description,
  .assembly-client-box p {
    font-size: 14px;
  }

  .assembly-client-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .assembly-details-actions {
    grid-template-columns: 1fr;
  }

  .assembly-details-actions {
    gap: 16px;
  }

  .assembly-details-text {
    justify-self: center;
  }
}
</style>
